import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Alert,
  darken,
  IconButton,
  lighten,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  useTheme
} from '@mui/material'
import { AlertPhone, RuuviTag, User, UserAlertRule } from '../types'
import { THEMES } from '../constants'
import { ExpandMore } from '@mui/icons-material'
import { formatMacAddress } from '../utils/string'
import AlertRuleDataGrid from './AlertRuleDataGrid'
import { useAccordion, useRuuviTagAlertTemplates } from '../hooks'
import { useEffect, useState } from 'react'
import { AlertGroup } from '../types/Alerts'
import { getRuuviTagAlertRules } from '../services/ruuviTagService'
import { AxiosRequestConfig } from 'axios'
import LoadingState from './LoadingState'
import AlertRuleAddButton from './AlertRuleAddButton'
import AlertTemplateDefaultsButton from './AlertTemplateDefaultsButton'
import { useTranslation } from 'react-i18next'
import RuuviTagStatusIndicator from './RuuviTagStatusIndicator'
import { getUserAlertRulesByRuuviTag } from '../services/userService'
import AlertPhoneAlertRulesDataGrid from './alertPhone/AlertPhoneAlertRulesDataGrid'
import { getAlertPhoneAlertRules } from '../services/alertPhoneService'

interface RuuviTagAlertRuleAccordionProps extends Omit<AccordionProps, 'children'> {
  ruuviTag: RuuviTag
  user?: User | null
  alertPhone?: AlertPhone | null
  alertPhoneAlertRules?: UserAlertRule[]
}

export default function RuuviTagAlertRuleAccordion({
  ruuviTag,
  user,
  alertPhone,
  alertPhoneAlertRules,
  sx,
  ...props
}: RuuviTagAlertRuleAccordionProps) {
  if (!user && !alertPhone) {
    throw new Error('Must provide either "user" or "alertPhone" prop.')
  }

  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The theme function.
   */
  const theme = useTheme()

  /**
   * The accordion state.
   */
  const { expanded, toggleAccordion } = useAccordion()

  /**
   * The alert templates.
   */
  const alertTemplates = useRuuviTagAlertTemplates()

  /**
   * The alert rules.
   */
  const [alertRules, setAlertRules] = useState<AlertGroup[]>([])

  /**
   * The user alert rules state.
   */
  const [userAlertRules, setUserAlertRules] = useState<UserAlertRule[]>([])

  /**
   * The enabled alert rules.
   */
  const enabledAlertRules = alertRules.filter((group) =>
    group.alerts.some((alert) => alert.enabled)
  )

  /**
   * The loading state.
   */
  const [isLoading, setLoading] = useState<boolean>(true)

  /**
   * The error state.
   */
  const [error, setError] = useState<unknown>()

  /**
   * Load the state for a user.
   */
  async function loadStateForUser(userId: number, ruuviTagId: string, config?: AxiosRequestConfig) {
    try {
      setLoading(true)
      await Promise.all([
        loadAlertRules(ruuviTagId, config),
        loadUserAlertRules(userId, ruuviTagId, config)
      ])
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Load the state for a user.
   */
  async function loadStateForAlertPhone(
    alertPhoneId: number,
    ruuviTagId: string,
    config?: AxiosRequestConfig
  ) {
    try {
      setLoading(true)
      await loadAlertRules(ruuviTagId, config)
      setUserAlertRules(
        alertPhoneAlertRules?.filter(
          (rule) => rule.alertPhoneId === alertPhoneId && rule.ruuviTagId === ruuviTagId
        ) ?? []
      )
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Load the alert rules.
   */
  async function loadAlertRules(ruuviTagId: string, config?: AxiosRequestConfig) {
    setAlertRules(await getRuuviTagAlertRules(ruuviTagId, config))
  }

  /**
   * Load the user alert rules.
   */
  async function loadUserAlertRules(
    userId: number,
    ruuviTagId: string,
    config?: AxiosRequestConfig
  ) {
    setUserAlertRules(await getUserAlertRulesByRuuviTag(userId, ruuviTagId, config))
  }

  /**
   * Load the state when the ruuvitag or the current user changes.
   */
  useEffect(() => {
    if (ruuviTag?.id) {
      const controller = new AbortController()

      if (user?.id) {
        loadStateForUser(user.id, ruuviTag.id, { signal: controller.signal })
      } else if (alertPhone?.id) {
        loadStateForAlertPhone(alertPhone.id, ruuviTag.id, { signal: controller.signal })
      }

      return () => controller.abort()
    }
  }, [ruuviTag.id, user?.id, alertPhone?.id])

  return (
    <Accordion
      expanded={expanded}
      sx={{
        ...sx,
        width: '100%',
        boxShadow: 0,
        '::before': { display: 'none' },
        '& .MuiAccordionSummary-root': {
          pl: 4,
          pr: 2,
          cursor: 'default',
          ':hover': {
            backgroundColor:
              theme.palette.mode === THEMES.DARK
                ? lighten(theme.palette.background.paper, 0.04)
                : darken(theme.palette.background.paper, 0.04)
          }
        }
      }}
      {...props}
    >
      <AccordionSummary
        onClick={toggleAccordion}
        expandIcon={
          <Tooltip title={expanded ? t('hideAlertRules') : t('showAlertRules')}>
            <IconButton>
              <ExpandMore />
            </IconButton>
          </Tooltip>
        }
      >
        <ListItem
          component="div"
          disablePadding
          sx={{
            p: 0,
            m: 0,
            width: '100%'
          }}
        >
          <ListItemIcon sx={{ minWidth: 24 }}>
            <RuuviTagStatusIndicator ruuviTag={ruuviTag} />
          </ListItemIcon>
          <ListItemText
            sx={{ width: 'auto', pr: 0 }}
            primary={ruuviTag?.asset?.name ?? ruuviTag.id}
            secondary={formatMacAddress(ruuviTag.id)}
            primaryTypographyProps={{
              sx: { order: '1 !important' }
            }}
            secondaryTypographyProps={{
              sx: { order: '2 !important' }
            }}
          />

          {!isLoading && expanded && !alertPhone && (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              onClick={(event) => event.stopPropagation()}
              sx={{ mr: 1 }}
            >
              <AlertTemplateDefaultsButton
                alertTemplates={alertTemplates}
                alertRules={alertRules}
                ruuviTag={ruuviTag}
                useRedux={false}
                afterUpdate={setAlertRules}
              />
              <AlertRuleAddButton
                alertTemplates={alertTemplates}
                alertRules={alertRules}
                ruuviTag={ruuviTag}
                useRedux={false}
                afterUpdate={setAlertRules}
              />
            </Stack>
          )}
        </ListItem>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 8, py: 2 }}>
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <Alert severity="error">{t('loadAlertRulesError')}</Alert>
        ) : enabledAlertRules.length === 0 ? (
          <Alert severity="info">{t('noAlertRules')}</Alert>
        ) : alertPhone ? (
          <AlertPhoneAlertRulesDataGrid
            alertPhone={alertPhone}
            ruuviTag={ruuviTag}
            alertPhoneAlertRules={userAlertRules}
            alertRules={alertRules}
            onUpdateAlertPhoneAlertRules={setUserAlertRules}
          />
        ) : (
          <AlertRuleDataGrid
            ruuviTag={ruuviTag}
            alertTemplates={alertTemplates}
            alertRules={alertRules}
            userAlertRules={userAlertRules}
            useRedux={false}
            afterUpdate={setAlertRules}
            afterUserAlertRulesUpdate={setUserAlertRules}
          />
        )}
      </AccordionDetails>
    </Accordion>
  )
}
